import type { ProductMapType, Product } from '@virginexperiencedays/products';
import type { BodySlice } from '@virginexperiencedays/cms';
import { cn } from '@virginexperiencedays/tailwind';
import type { ProductEmbedType } from '../ProductEmbed';
import { RichTextWrap } from '../../../typography/RichTextWrap';
import {
  StyledRichText,
  StyledRichTextProps,
} from '../../../typography/RichTextWrap/StyledRichText';
import { Divider } from '../../../layout/Divider';
import { ImageModule } from '../../../layout/ImageModule';
import { YoutubeEmbed } from '../../../layout/YoutubeEmbed';
import { DisplayHeading } from '../../../typography/DisplayHeading';
import { ProductEmbed } from '../ProductEmbed';
import { CallOutModule } from '../CallOutModule';

type PostBodyProps = {
  body: BodySlice[];
  products?: ProductMapType;
  location_title: string;
};

export const PostBody = ({ body, products, location_title }: PostBodyProps) => {
  if (!body?.length) {
    return null;
  }

  return (
    <div className="md:mx-auto md:max-w-[688px]">
      {body.map((slice, index) => {
        switch (slice.__typename) {
          case 'PostBodyImage_module':
          case 'Blog_post_pageBodyImage_module':
            return (
              <ImageModule
                key={index}
                images={slice.fields.map(({ image, caption }) => {
                  return {
                    imgUrl: image.url,
                    dimensions: image.dimensions,
                    alt: image.alt ?? location_title ?? '',
                    caption,
                  };
                })}
              />
            );
          case 'PostBodyProduct_embed':
          case 'Blog_post_pageBodyProduct_embed': {
            const embeds: ProductEmbedType[] = slice.fields.map(
              ({ product_id, title_override }) => {
                return { ...products[product_id], title_override };
              }
            );
            const invalid = embeds.map((product) => validate(product)).filter((id) => id);

            if (invalid.length) {
              console.warn(`post page '${location_title}' has invalid products`);
            }

            return (
              <ProductEmbed
                key={index}
                products={embeds.filter(
                  (product) => product.title && product.slug && product.status === 'active'
                )}
                variant={slice.primary.product_embed_type}
              />
            );
          }
          case 'PostBodyDivider':
          case 'Blog_post_pageBodyDivider':
            return (
              <div className="self-center py-10" key={index}>
                <Divider
                  variant={
                    slice.primary.divider_type === 'line' ? 'grey' : slice.primary.divider_type
                  }
                />
              </div>
            );
          case 'PostBodySubheading':
          case 'Blog_post_pageBodySubheading':
            return (
              <DisplayHeading className="py-3" size="2" key={index}>
                {slice.primary.sub_heading}
              </DisplayHeading>
            );
          case 'PostBodyContent_only':
          case 'Blog_post_pageBodyContent_only':
            return (
              <BlogStyledRichText key={index}>
                <RichTextWrap field={slice.primary.content as any} />
              </BlogStyledRichText>
            );
          case 'PostBodyCallout':
          case 'Blog_post_pageBodyCallout':
            return (
              <CallOutModule
                key={index}
                title={slice.primary.callout_title}
                content={slice.primary.content[0].text}
              />
            );
          case 'PostBodyEmbed_video':
          case 'Blog_post_pageBodyEmbed_video':
            return <YoutubeEmbed key={index} url={slice.primary.video_url} />;
          default:
            return null;
        }
      })}
    </div>
  );
};

function validate(product: Product) {
  const { id, title, url, price, media, slug } = product;

  if (!title || !url || !price?.displayPrice || !price?.rrp || !media?.images?.length || !slug)
    return id;
}

const BlogStyledRichText = ({ children, ...rest }: StyledRichTextProps) => (
  <StyledRichText
    className={cn(
      'leading-relaxed',
      '[&_h2]:leading-tight',
      '[&_h3]:font-semibold',
      '[&_h4]:text-base/base [&_h4]:font-serif [&_h4]:normal-case',
      '[&_h5]:leading-base',
      '[&_h6]:leading-base',
      '[&_ul]:list-none'
    )}
    {...rest}
  >
    {children}
  </StyledRichText>
);
